import React, { useEffect, useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import axios from "axios";
import {
  getDashboard, listData, executiveSummary, ga4Summary, adsSummary, dailyBrief,
  fmtMoney, fmtNum, fmtPct, API, bizTerms,
} from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Plus, X, DotsSixVertical, ArrowCounterClockwise, Check, ArrowRight, Sparkle,
  CalendarStar, ListChecks, Bell, Robot, CheckCircle, Circle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const Grid = WidthProvider(Responsive);
const fmtDate = (s) => new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const relTime = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};
const tip = { contentStyle: { background: "#10101A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10 } };

const NoData = ({ msg }) => (
  <div className="h-full flex items-center justify-center text-center text-xs text-[#7a7a7a] px-2" data-testid="widget-no-data">{msg}</div>
);

const Stat = (label, val, sub, subCls = "text-[#7a7a7a]") => (
  <div className="h-full flex flex-col justify-center">
    <div className="label-caps">{label}</div>
    <div className="font-head text-3xl font-semibold mt-2">{val}</div>
    {sub && <div className={`text-xs mt-1 ${subCls}`}>{sub}</div>}
  </div>
);

const Mini = ({ label, value }) => (
  <div className="rounded-lg bg-white/[0.04] border border-white/8 px-3 py-2.5 min-w-0">
    <div className="label-caps truncate">{label}</div>
    <div className="font-head text-lg font-semibold mt-0.5 truncate">{value}</div>
  </div>
);

const ListRow = ({ left, right, sub }) => (
  <div className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0 text-sm min-w-0">
    <div className="min-w-0">
      <div className="text-[#cfcfcf] truncate">{left}</div>
      {sub && <div className="text-[10px] text-[#7a7a7a] truncate">{sub}</div>}
    </div>
    {right && <span className="text-[11px] text-[#9a9a9a] shrink-0">{right}</span>}
  </div>
);

const GoTo = ({ label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="widget-x mt-auto pt-2 text-xs brand-text hover:text-[#A56CFF] font-semibold flex items-center gap-1 cursor-pointer">
    {label} <ArrowRight size={12} weight="bold" />
  </button>
);

function AssistantWidget({ ctx }) {
  const [prompt, setPrompt] = useState("");
  const go = (p) => p.trim() && ctx.navigate(`/director?prompt=${encodeURIComponent(p.trim())}`);
  return (
    <div className="h-full flex flex-col widget-x">
      <div className="flex items-center gap-2 mb-2"><Robot size={18} className="brand-text" weight="fill" /><span className="text-sm text-[#cfcfcf]">Tell your AI Marketing Director what you need.</span></div>
      <form onSubmit={(e) => { e.preventDefault(); go(prompt); }} className="flex gap-2">
        <input data-testid="assistant-widget-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. Run a weekend flash sale…"
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#6B3DFF]/50 placeholder:text-[#5f5f5f]" />
        <Button data-testid="assistant-widget-submit" type="submit" size="sm" className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white shrink-0">Go</Button>
      </form>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {["Promote my best seller", "Create an email campaign", "Plan this week's social posts"].map((p) => (
          <button key={p} data-testid={`assistant-quick-${p.split(" ")[0].toLowerCase()}`} onClick={() => go(p)}
            className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-[#9a9a9a] hover:text-white hover:border-[#6B3DFF]/40 transition-colors">{p}</button>
        ))}
      </div>
      <GoTo label="Open AI Director" onClick={() => ctx.navigate("/director")} testid="assistant-widget-open" />
    </div>
  );
}

// src drives the honest Live / Not connected / No data gating
const WIDGETS = {
  // ── Row 1: KPI stats ──
  execSummary: { title: "Executive Summary", w: 3, h: 2, src: "always",
    render: (c) => (
      <div className="h-full grid grid-cols-2 gap-2 content-center">
        <Mini label="Approvals" value={fmtNum(c.exec?.kpis?.pending_approvals)} />
        <Mini label="Scheduled" value={fmtNum(c.exec?.kpis?.scheduled_campaigns)} />
        <Mini label="AI Waiting" value={fmtNum(c.exec?.kpis?.ai_tasks_waiting)} />
        <Mini label="Alerts" value={fmtNum(c.exec?.kpis?.inventory_alerts)} />
      </div>
    ) },
  revenue: { title: "Revenue", w: 3, h: 2, src: "shopify",
    render: (c) => Stat("Revenue", fmtMoney(c.d.revenue), c.d.sources?.window_days ? `Shopify · last ${c.d.sources.window_days}d` : "Shopify") },
  adSpend: { title: "Ad Spend", w: 3, h: 2, src: "roas",
    render: (c) => Stat("Ad Spend", fmtMoney(c.d.total_ad_spend), "Meta + Google") },
  roas: { title: "ROAS", w: 3, h: 2, src: "roas",
    render: (c) => Stat("ROAS", `${c.d.roas}x`, `${fmtMoney(c.d.total_ad_spend)} spend`) },
  // ── Row 2: Operations ──
  calendar: { title: "Marketing Calendar", w: 4, h: 4, src: "always",
    render: (c) => (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 mb-2"><CalendarStar size={16} className="brand-text" /><span className="font-head text-xl font-semibold">{fmtNum(c.exec?.scheduled_campaigns)}</span><span className="text-xs text-[#9a9a9a]">scheduled</span></div>
        <div className="flex-1 min-h-0 overflow-hidden">
          {(c.exec?.scheduled_items || []).slice(0, 5).map((s, i) => <ListRow key={i} left={s.title} right={s.scheduled_at} sub={s.platform} />)}
          {!(c.exec?.scheduled_items || []).length && <NoData msg="Nothing scheduled yet — plan campaigns in the calendar." />}
        </div>
        <GoTo label="Open calendar" onClick={() => c.navigate("/scheduler")} testid="calendar-widget-open" />
      </div>
    ) },
  approvalQueue: { title: "Approval Queue", w: 4, h: 4, src: "always",
    render: (c) => {
      const pending = (c.approvals || []).filter((a) => ["pending", "draft"].includes(a.status));
      return (
        <div className="h-full flex flex-col">
          <div className="flex items-center gap-2 mb-2"><ListChecks size={16} className="brand-text" /><span className="font-head text-xl font-semibold">{pending.length}</span><span className="text-xs text-[#9a9a9a]">awaiting review</span></div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {pending.slice(0, 5).map((a, i) => <ListRow key={i} left={a.title} right={a.platform} sub={relTime(a.created_at)} />)}
            {!pending.length && <NoData msg="Queue is clear — generate a campaign to see drafts here." />}
          </div>
          <GoTo label="Review queue" onClick={() => c.navigate("/approvals")} testid="approvals-widget-open" />
        </div>
      );
    } },
  aiAssistant: { title: "AI Executive Assistant", w: 4, h: 4, src: "always",
    render: (c) => <AssistantWidget ctx={c} /> },
  // ── Row 3: Channel performance ──
  shopifyPerf: { title: "Shopify Performance", w: 4, h: 4, src: "shopify",
    render: (c) => (
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-3 gap-2 mb-2">
          <Mini label="Revenue" value={fmtMoney(c.d.revenue)} /><Mini label="Orders" value={fmtNum(c.d.orders)} /><Mini label="AOV" value={fmtMoney(c.d.aov)} />
        </div>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={c.d.revenue_series || []}>
              <defs><linearGradient id="spg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6B3DFF" stopOpacity={0.5} /><stop offset="100%" stopColor="#6B3DFF" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#5a5a5a" fontSize={9} tickLine={false} axisLine={false} />
              <YAxis hide /><Tooltip {...tip} labelFormatter={fmtDate} formatter={(v) => fmtMoney(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#6B3DFF" strokeWidth={2} fill="url(#spg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    ) },
  googleAnalytics: { title: "Google Analytics", w: 4, h: 4, src: "ga4",
    render: (c) => (
      <div className="h-full flex flex-col justify-center">
        <div className="grid grid-cols-2 gap-2">
          <Mini label="Sessions" value={fmtNum(c.ga4?.totals?.sessions)} />
          <Mini label="Users" value={fmtNum(c.ga4?.totals?.users)} />
          <Mini label="Conversions" value={fmtNum(c.ga4?.totals?.conversions)} />
          <Mini label="Revenue" value={fmtMoney(c.ga4?.totals?.revenue)} />
        </div>
        <div className="text-[10px] text-[#7a7a7a] mt-2">Last {c.ga4?.range?.days || 30} days · vs prev: sessions {c.ga4?.deltas?.sessions > 0 ? "+" : ""}{c.ga4?.deltas?.sessions ?? 0}%</div>
      </div>
    ) },
  metaAds: { title: "Meta Ads Performance", w: 4, h: 4, src: "meta",
    render: (c) => (
      <div className="h-full flex flex-col justify-center">
        <div className="grid grid-cols-2 gap-2">
          <Mini label="Spend" value={fmtMoney(c.meta?.totals?.spend)} />
          <Mini label="Revenue" value={fmtMoney(c.meta?.totals?.revenue)} />
          <Mini label="ROAS" value={`${c.meta?.totals?.roas ?? 0}x`} />
          <Mini label="Purchases" value={fmtNum(c.meta?.totals?.purchases)} />
        </div>
        <div className="text-[10px] text-[#7a7a7a] mt-2">Last {c.meta?.range?.days || 30} days · Meta only</div>
      </div>
    ) },
  // ── Row 4: Intelligence ──
  aiRecommendations: { title: "AI Recommendations", w: 4, h: 4, src: "always",
    render: (c) => (
      <div className="h-full flex flex-col">
        <div className="flex-1 min-h-0 overflow-hidden space-y-2">
          {c.briefLoading && <div className="text-xs text-[#9a9a9a] ai-thinking pt-2">Analyzing your store, ads & traffic…</div>}
          {!c.briefLoading && (c.brief?.recommended_actions || []).slice(0, 3).map((r, i) => (
            <div key={i} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/8">
              <div className="text-xs font-semibold flex items-start gap-1.5"><Sparkle size={12} className="brand-text mt-0.5 shrink-0" weight="fill" />{r.action}</div>
              {r.impact && <div className="text-[10px] brand-text mt-1 ml-4">{r.impact}</div>}
            </div>
          ))}
          {!c.briefLoading && !(c.brief?.recommended_actions || []).length && <NoData msg="No recommendations yet — refresh your daily brief on the home page." />}
        </div>
        <GoTo label="Full daily brief" onClick={() => c.navigate("/")} testid="recs-widget-open" />
      </div>
    ) },
  tasks: { title: "Tasks", w: 4, h: 4, src: "always",
    render: (c) => {
      const open = (c.tasks || []).filter((t) => t.status !== "done");
      const done = (c.tasks || []).length - open.length;
      return (
        <div className="h-full flex flex-col">
          <div className="flex items-center gap-2 mb-2 text-xs text-[#9a9a9a]"><CheckCircle size={14} className="text-emerald-400" /> {done}/{(c.tasks || []).length} done</div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {open.slice(0, 5).map((t, i) => <ListRow key={i} left={<span className="flex items-center gap-1.5"><Circle size={11} className="text-[#5a5a5a] shrink-0" />{t.title}</span>} sub={t.phase} />)}
            {!open.length && <NoData msg={(c.tasks || []).length ? "All tasks complete — nice work." : "No plan tasks yet — generate a 30-day plan."} />}
          </div>
          <GoTo label="Open 30-day plan" onClick={() => c.navigate("/plan")} testid="tasks-widget-open" />
        </div>
      );
    } },
  notifications: { title: "Notifications", w: 4, h: 4, src: "always",
    render: (c) => (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 mb-2"><Bell size={15} className="brand-text" /><span className="text-xs text-[#9a9a9a]">Latest system & Auto Pilot activity</span></div>
        <div className="flex-1 min-h-0 overflow-hidden">
          {(c.notifs || []).slice(0, 5).map((n, i) => <ListRow key={i} left={n.title} right={relTime(n.created_at)} sub={n.body} />)}
          {!(c.notifs || []).length && <NoData msg="No notifications yet — Auto Pilot events will appear here." />}
        </div>
      </div>
    ) },
  // ── Optional extras (off by default) ──
  orders: { title: "Orders", w: 3, h: 2, src: "shopify", render: (c) => Stat("Orders", fmtNum(c.d.orders), "Shopify") },
  aov: { title: "Avg Order Value", w: 3, h: 2, src: "shopify", render: (c) => Stat("AOV", fmtMoney(c.d.aov), "Shopify") },
  conversion: { title: "Conversion Rate", w: 3, h: 2, src: "ga4", render: (c) => Stat("Conversion", fmtPct(c.d.conversion_rate), "GA4") },
  traffic: { title: "Website Traffic", w: 3, h: 2, src: "ga4", render: (c) => Stat("Traffic", fmtNum(c.d.traffic), "GA4") },
  subscribers: { title: "Subscribers", w: 3, h: 2, src: "subs", render: (c) => Stat("Subscribers", fmtNum(c.d.active_subscribers), "Manual snapshots") },
  email: { title: "Email Revenue", w: 3, h: 2, src: "email", render: (c) => Stat("Email Rev", fmtMoney(c.d.email_revenue), "Manual log") },
  revenueChart: { title: "Revenue Trend", w: 8, h: 4, src: "shopify",
    render: (c) => (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={c.d.revenue_series}>
          <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6B3DFF" stopOpacity={0.5} /><stop offset="100%" stopColor="#6B3DFF" stopOpacity={0} /></linearGradient></defs>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#5a5a5a" fontSize={10} tickLine={false} axisLine={false} />
          <YAxis stroke="#5a5a5a" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
          <Tooltip {...tip} labelFormatter={fmtDate} formatter={(v) => fmtMoney(v)} />
          <Area type="monotone" dataKey="revenue" stroke="#6B3DFF" strokeWidth={2.5} fill="url(#rg)" />
        </AreaChart>
      </ResponsiveContainer>
    ) },
  roasChart: { title: "ROAS by Channel", w: 4, h: 4, src: "roas_chart",
    render: (c) => (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={c.d.roas_by_channel} layout="vertical" margin={{ left: 5 }}>
          <XAxis type="number" stroke="#5a5a5a" fontSize={10} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="channel" width={80} tick={{ fontSize: 9, fill: "#9a9a9a" }} tickLine={false} axisLine={false} />
          <Tooltip {...tip} formatter={(v) => `${v}x`} />
          <Bar dataKey="roas" fill="#6B3DFF" radius={[0, 4, 4, 0]} barSize={14} />
        </BarChart>
      </ResponsiveContainer>
    ) },
  subsChart: { title: "Subscription Growth", w: 6, h: 4, src: "subs",
    render: (c) => (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={c.d.subscription_series}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#5a5a5a" fontSize={10} tickLine={false} axisLine={false} />
          <YAxis stroke="#5a5a5a" fontSize={10} tickLine={false} axisLine={false} />
          <Tooltip {...tip} labelFormatter={fmtDate} />
          <Line type="monotone" dataKey="active_subscribers" stroke="#34D399" strokeWidth={2.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    ) },
  planProgress: { title: "30-Day Plan", w: 3, h: 3, src: "always",
    render: (c) => (
      <div className="h-full flex flex-col justify-center"><div className="font-head text-4xl font-semibold">{c.d.plan_pct}%</div><div className="text-sm text-[#9a9a9a] mt-1 mb-3">{c.d.plan_done}/{c.d.plan_total} tasks</div><Progress value={c.d.plan_pct} className="h-2 bg-white/10 [&>div]:bg-[#6B3DFF]" /></div>
    ) },
  checklistProgress: { title: "Conversion Checklist", w: 3, h: 3, src: "always",
    render: (c) => (
      <div className="h-full flex flex-col justify-center"><div className="font-head text-4xl font-semibold">{c.d.checklist_pct}%</div><div className="text-sm text-[#9a9a9a] mt-1 mb-3">{c.d.checklist_done}/{c.d.checklist_total} done</div><Progress value={c.d.checklist_pct} className="h-2 bg-white/10 [&>div]:bg-emerald-500" /></div>
    ) },
  bestSellers: { title: "Best Sellers", w: 6, h: 4, src: "shopify",
    render: (c) => (
      <div className="space-y-2">
        {(c.d.best_sellers || []).slice(0, 5).map((b, i) => (
          <div key={i} className="flex items-center justify-between text-sm"><span className="text-[#cfcfcf] truncate">{b.name}</span><span className="brand-text font-semibold">{b.units} units · {fmtMoney(b.revenue)}</span></div>
        ))}
        {!(c.d.best_sellers || []).length && <NoData msg="No sales in the sync window yet." />}
      </div>
    ) },
};

// Honest gating: returns null (render) or a NoData message
function sourceGate(w, c) {
  const s = c.d?.sources || {};
  switch (w.src) {
    case "shopify": return s.shopify ? null : "Not connected — connect Shopify to see live store data.";
    case "ga4": return s.ga4 ? null : "Not connected — connect Google Analytics to view traffic data.";
    case "meta": return c.meta?.connected?.meta ? null : "Not connected — connect Meta Ads to see live campaign performance.";
    case "roas": return (s.ads || s.manual_roas) ? null : "No data yet — connect Meta/Google Ads or log ROAS entries.";
    case "roas_chart": return (c.d?.roas_by_channel || []).length ? null : "No data yet — connect Meta/Google Ads or log ROAS entries.";
    case "subs": return s.manual_subs ? null : "No subscription data — add snapshots on the Customers page.";
    case "email": return (s.klaviyo || s.manual_email) ? null : "No data yet — connect Klaviyo or log campaigns manually.";
    default: return null;
  }
}

const srcLabel = {
  shopify: "Shopify", ga4: "Google Analytics 4", meta: "Meta Ads", roas: "Ads / manual ROAS",
  roas_chart: "Ads / manual ROAS", subs: "Manual snapshots", email: "Klaviyo / manual", always: "Workspace",
};

// Strict 4-row professional default layout (12-col grid)
const DEFAULT_LAYOUT = [
  { i: "execSummary", x: 0, y: 0 }, { i: "revenue", x: 3, y: 0 }, { i: "adSpend", x: 6, y: 0 }, { i: "roas", x: 9, y: 0 },
  { i: "calendar", x: 0, y: 2 }, { i: "approvalQueue", x: 4, y: 2 }, { i: "aiAssistant", x: 8, y: 2 },
  { i: "shopifyPerf", x: 0, y: 6 }, { i: "googleAnalytics", x: 4, y: 6 }, { i: "metaAds", x: 8, y: 6 },
  { i: "aiRecommendations", x: 0, y: 10 }, { i: "tasks", x: 4, y: 10 }, { i: "notifications", x: 8, y: 10 },
].map((l) => ({ ...l, w: WIDGETS[l.i].w, h: WIDGETS[l.i].h }));

// Normalize any stored layout: valid widget ids, canonical fixed sizes, snapped into 12 cols
const normalize = (arr) => (Array.isArray(arr) ? arr : [])
  .filter((l) => l && WIDGETS[l.i] && Number.isFinite(l.x) && Number.isFinite(l.y))
  .map((l) => {
    const w = WIDGETS[l.i].w, h = WIDGETS[l.i].h;
    return { i: l.i, w, h, x: Math.min(Math.max(0, Math.round(l.x)), 12 - w), y: Math.max(0, Math.round(l.y)) };
  });

export default function DashboardGrid() {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const terms = bizTerms(activeClient);
  const wTitle = (k) => ({ orders: terms.orders, aov: terms.aov, bestSellers: terms.bestSellers }[k] || WIDGETS[k].title);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [bp, setBp] = useState("lg");
  const savedToast = useRef(0);
  const present = useMemo(() => new Set(layout.map((l) => l.i)), [layout]);
  const has = (id) => present.has(id);

  const { data: d } = useQuery({ queryKey: ["dashboard", activeId], queryFn: () => getDashboard(activeId), enabled: !!activeId });
  const { data: exec } = useQuery({ queryKey: ["executive", activeId], queryFn: () => executiveSummary(activeId), enabled: !!activeId && (has("execSummary") || has("calendar")) });
  const { data: approvals = [] } = useQuery({ queryKey: ["data", "approvals", activeId], queryFn: () => listData("approvals", activeId), enabled: !!activeId && has("approvalQueue") });
  const { data: tasks = [] } = useQuery({ queryKey: ["data", "plan_tasks", activeId], queryFn: () => listData("plan_tasks", activeId), enabled: !!activeId && has("tasks") });
  const { data: ga4 } = useQuery({ queryKey: ["ga4-summary", activeId], queryFn: () => ga4Summary(activeId), enabled: !!activeId && has("googleAnalytics") && !!d?.sources?.ga4 });
  const { data: meta } = useQuery({ queryKey: ["ads-summary", "meta", activeId], queryFn: () => adsSummary(activeId, { platform: "meta" }), enabled: !!activeId && has("metaAds") });
  const { data: auto } = useQuery({ queryKey: ["autopilot-status", activeId], queryFn: () => axios.get(`${API}/autopilot/status`, { params: { client_id: activeId } }).then((r) => r.data), enabled: !!activeId && has("notifications") });
  const { data: brief, isLoading: briefLoading } = useQuery({ queryKey: ["daily-brief", activeId], queryFn: () => dailyBrief(activeId), enabled: !!activeId && has("aiRecommendations"), staleTime: 15 * 60 * 1000 });

  useEffect(() => {
    if (!activeId) return;
    axios.get(`${API}/dashboard/layout`, { params: { client_id: activeId } }).then((r) => {
      const raw = r.data.layout;
      // v2 = post grid-refactor layouts; older free-form layouts are discarded for the clean default
      const loaded = Array.isArray(raw) && raw.length && raw[0].v === 2 ? normalize(raw) : [];
      setLayout(loaded.length ? loaded : DEFAULT_LAYOUT);
    }).catch(() => setLayout(DEFAULT_LAYOUT));
  }, [activeId]);

  const persist = (next, silent = false) => {
    const cleaned = normalize(next);
    setLayout(cleaned);
    if (activeId) {
      axios.put(`${API}/dashboard/layout`, { client_id: activeId, layout: cleaned.map((l) => ({ ...l, v: 2 })) }).then(() => {
        if (!silent && Date.now() - savedToast.current > 1500) {
          savedToast.current = Date.now();
          toast.success("Layout saved", { duration: 1200 });
        }
      });
    }
  };
  const onDragStop = (l) => persist(l);
  const removeWidget = (id) => persist(layout.filter((l) => l.i !== id), true);
  const addWidget = (id) => {
    if (has(id)) return;
    const maxY = layout.reduce((m, l) => Math.max(m, l.y + l.h), 0);
    persist([...layout, { i: id, x: 0, y: maxY, w: WIDGETS[id].w, h: WIDGETS[id].h }], true);
  };
  const toggleWidget = (id) => (has(id) ? removeWidget(id) : addWidget(id));
  const reset = () => {
    if (window.confirm("Reset the dashboard to the default layout? Your custom arrangement will be lost.")) {
      persist(DEFAULT_LAYOUT, true);
      toast.success("Layout reset to default");
    }
  };

  const ctx = { d: d || {}, exec, approvals, tasks, ga4, meta, notifs: auto?.notifications, brief, briefLoading, navigate };
  const draggable = bp === "lg" || bp === "md";

  if (!d) return <PageContainer><div className="text-[#7a7a7a] py-20 text-center">Loading dashboard…</div></PageContainer>;

  return (
    <PageContainer>
      <PageHeader eyebrow="Customizable" title="Widget Board"
        subtitle="Drag widgets by their title bar to reorder — they snap into a clean grid and your layout saves automatically.">
        <Button data-testid="reset-layout-btn" variant="outline" onClick={reset} className="border-white/15 bg-transparent hover:bg-white/5 text-white"><ArrowCounterClockwise size={16} className="mr-1.5" /> Reset Layout</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="widget-toggles">
        {Object.keys(WIDGETS).map((k) => {
          const on = has(k);
          return (
            <button key={k} data-testid={`toggle-widget-${k}`} onClick={() => toggleWidget(k)}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${on ? "border-[#6B3DFF]/40 bg-[#6B3DFF]/12 text-white" : "border-white/10 text-[#8a8a8a] hover:text-white hover:border-white/25"}`}>
              {on ? <Check size={13} className="text-emerald-400" weight="bold" /> : <Plus size={13} />} {wTitle(k)}
            </button>
          );
        })}
      </div>

      <Grid className="layout" layouts={{ lg: layout, md: layout }}
        breakpoints={{ lg: 996, md: 768, sm: 480, xs: 0 }} cols={{ lg: 12, md: 12, sm: 6, xs: 4 }}
        rowHeight={78} margin={[16, 16]}
        compactType="vertical" preventCollision={false}
        isResizable={false} isDraggable={draggable} isBounded
        draggableHandle=".widget-drag" draggableCancel=".widget-x"
        onBreakpointChange={(b) => setBp(b)}
        onDragStop={(l) => draggable && onDragStop(l)}>
        {layout.map((item) => {
          const w = WIDGETS[item.i];
          if (!w) return <div key={item.i} />;
          const gate = sourceGate(w, ctx);
          return (
            <div key={item.i} className="card-matte overflow-hidden flex flex-col" data-testid={`widget-${item.i}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/8">
                <div className="widget-drag cursor-move flex-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#8a8a8a] select-none"
                  title={`Drag to reorder · Source: ${srcLabel[w.src] || "Workspace"}`} data-testid={`drag-handle-${item.i}`}>
                  <DotsSixVertical size={14} /> {wTitle(item.i)}
                </div>
                <button data-testid={`remove-widget-${item.i}`} className="widget-x text-[#7a7a7a] hover:text-red-400 pl-2 cursor-pointer" onClick={() => removeWidget(item.i)}><X size={14} /></button>
              </div>
              <div className="flex-1 p-4 min-h-0">{gate ? <NoData msg={gate} /> : w.render(ctx)}</div>
            </div>
          );
        })}
      </Grid>
    </PageContainer>
  );
}
